import db from "./db";

const toNum = (v) => Number(v);

// ── Sessions ──────────────────────────────────────────────────────────────────

export async function getSessions() {
  const sessions = await db.sessions.toArray();
  return { success: true, sessions };
}

// ── Class (results / course reg / professionals) ──────────────────────────────

export async function getClass({ class_id, session, semester, level }) {
  const sem = toNum(semester);
  const lvl = toNum(level);
  const classIdStr = String(class_id);

  // Find the session that contains this class
  // Sessions are stored with classes as objects with _id strings
  const allSessions = await db.sessions.toArray();
  const sessionDoc = allSessions.find((s) =>
    (s.classes || []).some((c) => String(c._id) === classIdStr),
  );

  if (!sessionDoc) {
    console.warn(
      "[offlineQuery.getClass] session not found for class_id",
      classIdStr,
    );
    return { success: true, students: [] };
  }

  const classDoc = (sessionDoc.classes || []).find(
    (c) => String(c._id) === classIdStr,
  );

  if (!classDoc) return { success: true, students: [] };

  // classDoc.students is an array of student _id strings (normalised in syncManager)
  const studentIds = new Set((classDoc.students || []).map(String));

  if (studentIds.size === 0) return { success: true, students: [] };

  // Get semester results — student_id is stored as string (normalised in syncManager)
  const semResults = await db.results
    .where("session")
    .equals(session)
    .filter(
      (r) =>
        toNum(r.semester) === sem &&
        toNum(r.level) === lvl &&
        studentIds.has(String(r.student_id)),
    )
    .toArray();

  // Get student details
  const studentIdsArr = [...studentIds];
  const studentDocs = await db.students
    .where("_id")
    .anyOf(studentIdsArr)
    .toArray();
  const studentMap = new Map(studentDocs.map((s) => [String(s._id), s]));

  const students = semResults.map((result) => {
    const student = studentMap.get(String(result.student_id)) || {};
    return {
      student_id: result.student_id,
      fullname: student.fullname || "",
      reg_no: student.reg_no || "",
      level: result.level,
      cgpa: student.cgpa ?? 0,
      gpa: result.gpa,
      session_gpa: result.session_gpa,
      semester: result.semester,
      session: result.session,
      courses: result.courses || [],
    };
  });

  return { success: true, students };
}

// ── Student semester results (transcript / student profile) ───────────────────

export async function getStudentResults(student_id) {
  const idStr = String(student_id);

  const results = await db.results.where("student_id").equals(idStr).toArray();

  const student = await db.students.get(idStr);
  if (!student) return [];

  return results
    .sort((a, b) => {
      const sc = String(a.session).localeCompare(String(b.session));
      if (sc !== 0) return sc;
      return toNum(a.semester) - toNum(b.semester);
    })
    .map((r) => ({ ...r, student_id: student }));
}

// ── Probation list ────────────────────────────────────────────────────────────

export async function getProbationList({ class_id, level, session }) {
  const lvl = toNum(level);
  const classIdStr = String(class_id);

  const allSessions = await db.sessions.toArray();
  const sessionDoc = allSessions.find((s) =>
    (s.classes || []).some((c) => String(c._id) === classIdStr),
  );
  if (!sessionDoc) return { students: [] };

  const classDoc = (sessionDoc.classes || []).find(
    (c) => String(c._id) === classIdStr,
  );
  if (!classDoc) return { students: [] };

  const studentIds = new Set((classDoc.students || []).map(String));
  if (studentIds.size === 0) return { students: [] };

  const semResults = await db.results
    .where("session")
    .equals(session)
    .filter(
      (r) =>
        toNum(r.semester) === 2 &&
        toNum(r.level) === lvl &&
        studentIds.has(String(r.student_id)),
    )
    .toArray();

  const studentDocs = await db.students
    .where("_id")
    .anyOf([...studentIds])
    .toArray();
  const studentMap = new Map(studentDocs.map((s) => [String(s._id), s]));

  const probation = semResults.filter((r) => r.session_gpa < 2.5);

  return {
    students: probation.map((r) => {
      const s = studentMap.get(String(r.student_id)) || {};
      return {
        student_id: r.student_id,
        fullname: s.fullname || "",
        reg_no: s.reg_no || "",
        level: s.level,
        session_gpa: r.session_gpa,
        session: r.session,
        moe: s.moe || "",
      };
    }),
  };
}

// ── Catalogue ─────────────────────────────────────────────────────────────────

export async function getCatalogue() {
  const [courses, departments] = await Promise.all([
    db.courses.toArray(),
    db.departments.toArray(),
  ]);
  return { success: true, courses, departments };
}

// ── Student search ────────────────────────────────────────────────────────────

export async function searchStudent(reg_no) {
  const q = String(reg_no || "").toLowerCase();
  const students = await db.students
    .filter((s) =>
      String(s.reg_no || "")
        .toLowerCase()
        .includes(q),
    )
    .toArray();
  return { student: students };
}
